<script setup>
import { computed } from "vue";
import { useStore } from "vuex";

const store = useStore();
const canAdd = computed(() => store.getters.hasPermission("expense.add"));
const canEdit = computed(() => store.getters.hasPermission("expense.edit"));
const canDelete = computed(() => store.getters.hasPermission("expense.delete"));
</script>

<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <div class="ex-head">
          <div>
            <h3>Expenses</h3>
            <p>Shop running costs — rent, salaries, electricity, transport and more.</p>
          </div>
          <div class="ex-head-actions">
            <router-link to="/expensecategory" class="ex-btn-light"><i class="fas fa-tags"></i> Categories</router-link>
            <button type="button" class="ex-btn-light" :disabled="exportLoading" title="Download as Excel" @click="exportToExcel">
              <i class="fas" :class="exportLoading ? 'fa-spinner fa-spin' : 'fa-file-excel'"></i> Export
            </button>
            <router-link v-if="canAdd" to="/createexpense" class="ex-btn-primary"><i class="fas fa-plus"></i> Add expense</router-link>
          </div>
        </div>

        <!-- Period -->
        <div class="ex-period">
          <div class="ex-chips">
            <button v-for="preset in presets" :key="preset.key" type="button" class="ex-chip" :class="{ on: period === preset.key }" @click="setPeriod(preset.key)">
              {{ preset.label }}
            </button>
          </div>
          <div class="ex-range">
            <input v-model="filters.date_from" type="date" aria-label="From date" @change="period = 'custom'; loadExpenses()" />
            <span>to</span>
            <input v-model="filters.date_to" type="date" aria-label="To date" @change="period = 'custom'; loadExpenses()" />
          </div>
        </div>

        <!-- KPIs -->
        <div class="ex-kpis">
          <div class="ex-kpi ex-kpi-main">
            <span>Total spent</span>
            <strong>{{ money(totalAmount) }}</strong>
            <small>{{ periodLabel }}</small>
          </div>
          <div class="ex-kpi">
            <span>Entries</span>
            <strong>{{ expenses.length }}</strong>
            <small>avg {{ money(expenses.length ? totalAmount / expenses.length : 0) }} each</small>
          </div>
          <div class="ex-kpi">
            <span>Per day</span>
            <strong>{{ money(perDay) }}</strong>
            <small>over {{ dayCount }} {{ dayCount === 1 ? "day" : "days" }}</small>
          </div>
          <div class="ex-kpi">
            <span>Biggest category</span>
            <strong class="ex-kpi-text">{{ byCategory[0] ? byCategory[0].name : "—" }}</strong>
            <small v-if="byCategory[0]">{{ money(byCategory[0].amount) }} &middot; {{ byCategory[0].share }}%</small>
          </div>
        </div>

        <div class="ex-layout">
          <div class="ex-card ex-main">
            <div class="ex-filters">
              <div class="ex-search">
                <i class="fas fa-search"></i>
                <input v-model.trim="filters.search" type="search" placeholder="Search payee, bill no or remarks..." @input="debouncedLoad" />
              </div>
              <select v-model="filters.categoryid" class="ex-select" aria-label="Category" @change="loadExpenses">
                <option value="">All categories</option>
                <option v-for="category in categories" :key="category.categoryid" :value="category.categoryid">{{ category.categoryname }}</option>
              </select>
              <div class="ex-modes" role="group" aria-label="Payment mode">
                <button type="button" :class="{ on: !filters.paymentmode }" @click="setMode('')">All</button>
                <button v-for="mode in paymentModes" :key="mode.value" type="button" :class="{ on: filters.paymentmode === mode.value }" :title="mode.label" @click="setMode(mode.value)">
                  <i :class="mode.icon"></i><span>{{ mode.short }}</span>
                </button>
              </div>
              <button v-if="hasFilters" type="button" class="ex-clear" @click="clearFilters">Clear</button>
            </div>

            <div v-if="loading" class="ex-empty"><i class="fas fa-spinner fa-spin"></i> Loading expenses...</div>
            <div v-else-if="!expenses.length" class="ex-empty">
              <div class="ex-empty-icon"><i class="fas fa-receipt"></i></div>
              <strong>{{ hasFilters ? "No expenses match these filters" : "No expenses in this period" }}</strong>
              <span v-if="!hasFilters">Record rent, salaries, electricity and other costs to see where the money goes.</span>
              <router-link v-if="canAdd && !hasFilters" to="/createexpense" class="ex-btn-primary mt-2"><i class="fas fa-plus"></i> Add expense</router-link>
            </div>
            <div v-else class="ex-list">
              <template v-for="group in groupedByDate" :key="group.date">
                <div class="ex-day">
                  <span>{{ dayLabel(group.date) }}</span>
                  <strong>{{ money(group.total) }}</strong>
                </div>
                <div v-for="expense in group.items" :key="expense.expenseid" class="ex-row">
                  <span class="ex-dot" :style="{ background: categoryColor(expense.categoryid) }"></span>
                  <div class="ex-row-main">
                    <div class="ex-row-title">
                      <strong>{{ expense.categoryname }}</strong>
                      <span v-if="expense.payeename" class="ex-payee">{{ expense.payeename }}</span>
                    </div>
                    <div class="ex-row-sub">
                      <span class="ex-mode"><i :class="modeOf(expense.paymentmode).icon"></i> {{ modeOf(expense.paymentmode).label }}</span>
                      <span v-if="expense.billno">&middot; Bill {{ expense.billno }}</span>
                      <span v-if="expense.remarks" class="ex-remark">&middot; {{ expense.remarks }}</span>
                    </div>
                  </div>
                  <strong class="ex-amount">{{ money(expense.amount) }}</strong>
                  <div v-if="canEdit || canDelete" class="ex-row-actions">
                    <router-link v-if="canEdit" :to="{ path: '/editexpense', query: { id: expense.expenseid } }" class="ex-icon-btn" title="Edit" aria-label="Edit"><i class="fas fa-pen"></i></router-link>
                    <button v-if="canDelete" type="button" class="ex-icon-btn danger" title="Delete" aria-label="Delete" @click="deleteExpense(expense)"><i class="fas fa-trash-alt"></i></button>
                  </div>
                </div>
              </template>
            </div>
          </div>

          <aside class="ex-card ex-side">
            <h5>Where the money went</h5>
            <div v-if="!byCategory.length" class="ex-side-empty">Nothing to show yet.</div>
            <button
              v-for="category in byCategory"
              :key="category.id"
              type="button"
              class="ex-cat"
              :class="{ on: filters.categoryid === category.id }"
              :title="`Show only ${category.name}`"
              @click="filters.categoryid = filters.categoryid === category.id ? '' : category.id; loadExpenses()"
            >
              <div class="ex-cat-top">
                <span><i class="ex-dot" :style="{ background: categoryColor(category.id) }"></i> {{ category.name }}</span>
                <strong>{{ money(category.amount) }}</strong>
              </div>
              <div class="ex-bar"><span :style="{ width: `${category.share}%`, background: categoryColor(category.id) }"></span></div>
              <small>{{ category.count }} {{ category.count === 1 ? "entry" : "entries" }} &middot; {{ category.share }}%</small>
            </button>
            <div v-if="byMode.length" class="ex-modes-summary">
              <h6>By payment</h6>
              <div v-for="mode in byMode" :key="mode.value" class="ex-mode-row">
                <span><i :class="mode.icon"></i> {{ mode.label }}</span>
                <strong>{{ money(mode.amount) }}</strong>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import { PAYMENT_MODES, paymentModeOf, categoryColor, isoDate } from "@/views/pages/expense/expenseShared";

function startOfWeek(date) {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7; // Monday = 0
  d.setDate(d.getDate() - day);
  return d;
}

export default {
  name: "expenselist",
  data() {
    const now = new Date();
    return {
      expenses: [],
      categories: [],
      loading: false,
      exportLoading: false,
      paymentModes: PAYMENT_MODES,
      period: "month",
      searchTimer: null,
      filters: {
        search: "",
        categoryid: "",
        paymentmode: "",
        date_from: isoDate(new Date(now.getFullYear(), now.getMonth(), 1)),
        date_to: isoDate(now),
      },
      presets: [
        { key: "today", label: "Today" },
        { key: "week", label: "This week" },
        { key: "month", label: "This month" },
        { key: "lastmonth", label: "Last month" },
        { key: "year", label: "This year" },
      ],
    };
  },
  computed: {
    totalAmount() {
      return this.expenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0);
    },
    dayCount() {
      const from = new Date(this.filters.date_from || isoDate(new Date()));
      const to = new Date(this.filters.date_to || isoDate(new Date()));
      return Math.max(1, Math.round((to - from) / 86400000) + 1);
    },
    perDay() {
      return this.totalAmount / this.dayCount;
    },
    periodLabel() {
      const preset = this.presets.find((p) => p.key === this.period);
      return preset ? preset.label : `${this.shortDate(this.filters.date_from)} – ${this.shortDate(this.filters.date_to)}`;
    },
    hasFilters() {
      return Boolean(this.filters.search || this.filters.categoryid || this.filters.paymentmode);
    },
    byCategory() {
      const map = new Map();
      this.expenses.forEach((expense) => {
        const row = map.get(expense.categoryid) || { id: expense.categoryid, name: expense.categoryname, amount: 0, count: 0 };
        row.amount += Number(expense.amount || 0);
        row.count += 1;
        map.set(expense.categoryid, row);
      });
      const total = this.totalAmount || 1;
      return [...map.values()]
        .sort((a, b) => b.amount - a.amount)
        .map((row) => ({ ...row, share: Math.round((row.amount / total) * 100) }));
    },
    byMode() {
      const map = new Map();
      this.expenses.forEach((expense) => {
        const mode = paymentModeOf(expense.paymentmode);
        const row = map.get(mode.value) || { ...mode, amount: 0 };
        row.amount += Number(expense.amount || 0);
        map.set(mode.value, row);
      });
      return [...map.values()].sort((a, b) => b.amount - a.amount);
    },
    groupedByDate() {
      const groups = new Map();
      [...this.expenses]
        .sort((a, b) => (b.expensedate || "").localeCompare(a.expensedate || ""))
        .forEach((expense) => {
          const group = groups.get(expense.expensedate) || { date: expense.expensedate, items: [], total: 0 };
          group.items.push(expense);
          group.total += Number(expense.amount || 0);
          groups.set(expense.expensedate, group);
        });
      return [...groups.values()];
    },
  },
  methods: {
    categoryColor,
    modeOf: paymentModeOf,
    setPeriod(key) {
      const now = new Date();
      const ranges = {
        today: [now, now],
        week: [startOfWeek(now), now],
        month: [new Date(now.getFullYear(), now.getMonth(), 1), now],
        lastmonth: [new Date(now.getFullYear(), now.getMonth() - 1, 1), new Date(now.getFullYear(), now.getMonth(), 0)],
        year: [new Date(now.getFullYear(), 0, 1), now],
      };
      const [from, to] = ranges[key];
      this.period = key;
      this.filters.date_from = isoDate(from);
      this.filters.date_to = isoDate(to);
      this.loadExpenses();
    },
    setMode(mode) {
      this.filters.paymentmode = mode;
      this.loadExpenses();
    },
    clearFilters() {
      this.filters.search = "";
      this.filters.categoryid = "";
      this.filters.paymentmode = "";
      this.loadExpenses();
    },
    debouncedLoad() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.loadExpenses, 300);
    },
    buildFilterParams() {
      const params = {};
      Object.keys(this.filters).forEach((key) => {
        if (this.filters[key]) params[key] = this.filters[key];
      });
      return params;
    },
    money(value) {
      return `Rs. ${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
    shortDate(value) {
      if (!value) return "";
      return new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
    },
    dayLabel(value) {
      const today = isoDate(new Date());
      const yesterday = isoDate(new Date(Date.now() - 86400000));
      if (value === today) return "Today";
      if (value === yesterday) return "Yesterday";
      return new Date(value).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
    },
    async loadCategories() {
      try {
        const response = await axios.get("/v1/expenses/categories");
        this.categories = response.data || [];
      } catch (error) {
        this.categories = [];
      }
    },
    async loadExpenses() {
      this.loading = true;
      try {
        const response = await axios.get("/v1/expenses", { params: this.buildFilterParams() });
        this.expenses = response.data || [];
      } catch (error) {
        Swal.fire({ icon: "error", title: "Failed to load expenses", text: error.response?.data?.detail || error.message });
      } finally {
        this.loading = false;
      }
    },
    async exportToExcel() {
      this.exportLoading = true;
      try {
        const response = await axios.get("/v1/expenses/export", { params: this.buildFilterParams(), responseType: "blob" });
        const blob = new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `expenses_${this.filters.date_from}_${this.filters.date_to}.xlsx`;
        link.click();
        URL.revokeObjectURL(link.href);
      } catch (error) {
        Swal.fire({ icon: "error", title: "Excel export failed", text: error.response?.data?.detail || error.message });
      } finally {
        this.exportLoading = false;
      }
    },
    async deleteExpense(expense) {
      const result = await Swal.fire({
        icon: "warning",
        title: "Delete this expense?",
        html: `<strong>${this.money(expense.amount)}</strong> &middot; ${String(expense.categoryname || "").replace(/[<>&]/g, "")}<br><small>This can't be undone.</small>`,
        showCancelButton: true,
        confirmButtonText: "Yes, delete",
        confirmButtonColor: "#dc2626",
      });
      if (!result.isConfirmed) return;
      try {
        await axios.delete(`/v1/expenses/${expense.expenseid}`);
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Expense deleted", showConfirmButton: false, timer: 1500 });
        await this.loadExpenses();
      } catch (error) {
        Swal.fire({ icon: "error", title: "Could not delete", text: error.response?.data?.detail || error.message });
      }
    },
  },
  async mounted() {
    if (this.$route.query.categoryid) this.filters.categoryid = String(this.$route.query.categoryid);
    await Promise.all([this.loadCategories(), this.loadExpenses()]);
  },
  beforeUnmount() {
    clearTimeout(this.searchTimer);
  },
};
</script>

<style scoped>
.ex-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 14px; flex-wrap: wrap; margin-bottom: 16px; }
.ex-head h3 { margin: 0; font-size: 20px; font-weight: 700; color: #111827; }
.ex-head p { margin: 2px 0 0; font-size: 13px; color: #6b7280; }
.ex-head-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.ex-btn-primary, .ex-btn-light { display: inline-flex; align-items: center; gap: 8px; height: 40px; padding: 0 16px; border-radius: 10px; font-size: 14px; font-weight: 600; white-space: nowrap; }
.ex-btn-primary { background: #FF9F43; color: #fff; border: 0; }
.ex-btn-primary:hover { background: #f08c2a; color: #fff; }
.ex-btn-light { background: #fff; color: #374151; border: 1px solid #e5e7eb; }
.ex-btn-light:hover { border-color: #FF9F43; color: #c2410c; }
.ex-btn-light .fa-file-excel { color: #16a34a; }

.ex-period { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-bottom: 14px; }
.ex-chips { display: flex; gap: 6px; flex-wrap: wrap; }
.ex-chip { border: 1px solid #e5e7eb; background: #fff; border-radius: 999px; padding: 6px 14px; font-size: 13px; font-weight: 600; color: #4b5563; }
.ex-chip.on { background: #111827; border-color: #111827; color: #fff; }
.ex-range { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #6b7280; }
.ex-range input { height: 36px; border: 1px solid #e5e7eb; border-radius: 8px; padding: 0 10px; font-size: 13px; background: #fff; }

.ex-kpis { display: grid; grid-template-columns: 1.3fr 1fr 1fr 1fr; gap: 12px; margin-bottom: 16px; }
.ex-kpi { display: flex; flex-direction: column; gap: 2px; padding: 14px 16px; border-radius: 14px; background: #fff; border: 1px solid #e9ecf2; min-width: 0; }
.ex-kpi span { font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: #6b7280; }
.ex-kpi strong { font-size: 22px; color: #111827; font-variant-numeric: tabular-nums; }
.ex-kpi small { font-size: 12px; color: #9ca3af; }
.ex-kpi-text { font-size: 18px !important; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ex-kpi-main { background: linear-gradient(135deg, #fff7ed, #ffedd5); border-color: #fed7aa; }
.ex-kpi-main strong { font-size: 26px; color: #9a3412; }

.ex-layout { display: grid; grid-template-columns: minmax(0, 1fr) 320px; gap: 16px; align-items: start; }
.ex-card { background: #fff; border: 1px solid #e9ecf2; border-radius: 14px; }
.ex-main { padding: 14px 16px; min-width: 0; }
.ex-filters { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 12px; }
.ex-search { position: relative; flex: 1 1 220px; }
.ex-search i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #9ca3af; }
.ex-search input { width: 100%; height: 38px; padding-left: 34px; border: 1px solid #e5e7eb; border-radius: 10px; font-size: 13.5px; }
.ex-search input:focus, .ex-select:focus { outline: none; border-color: #FF9F43; }
.ex-select { height: 38px; border: 1px solid #e5e7eb; border-radius: 10px; padding: 0 10px; font-size: 13.5px; background: #fff; min-width: 170px; }
.ex-modes { display: inline-flex; padding: 3px; border-radius: 10px; background: #f3f4f6; gap: 2px; flex-wrap: wrap; }
.ex-modes button { border: 0; background: transparent; padding: 6px 10px; border-radius: 8px; font-size: 12.5px; font-weight: 600; color: #6b7280; display: inline-flex; align-items: center; gap: 5px; }
.ex-modes button.on { background: #fff; color: #c2410c; box-shadow: 0 1px 3px rgba(15, 23, 42, 0.1); }
.ex-clear { border: 0; background: transparent; color: #c2410c; font-size: 13px; font-weight: 600; }

.ex-list { border: 1px solid #f1f2f4; border-radius: 12px; overflow: hidden; }
.ex-day { display: flex; justify-content: space-between; align-items: center; padding: 8px 14px; background: #f9fafb; font-size: 12.5px; font-weight: 600; color: #6b7280; border-bottom: 1px solid #f1f2f4; }
.ex-day strong { color: #374151; font-variant-numeric: tabular-nums; }
.ex-row { display: flex; align-items: center; gap: 12px; padding: 12px 14px; border-bottom: 1px solid #f3f4f6; }
.ex-row:last-child { border-bottom: 0; }
.ex-row:hover { background: #fffbf7; }
.ex-dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; flex: 0 0 10px; }
.ex-row-main { flex: 1; min-width: 0; }
.ex-row-title { display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; }
.ex-row-title strong { font-size: 14px; color: #111827; }
.ex-payee { font-size: 13px; color: #4b5563; }
.ex-row-sub { display: flex; gap: 6px; font-size: 12px; color: #9ca3af; margin-top: 2px; min-width: 0; }
.ex-mode i { margin-right: 2px; }
.ex-remark { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ex-amount { font-size: 15px; color: #111827; font-variant-numeric: tabular-nums; white-space: nowrap; }
.ex-row-actions { display: flex; gap: 4px; opacity: 0.55; transition: opacity 0.15s ease; }
.ex-row:hover .ex-row-actions { opacity: 1; }
.ex-icon-btn { width: 32px; height: 32px; border: 0; border-radius: 8px; background: #f3f4f6; color: #4b5563; display: inline-flex; align-items: center; justify-content: center; font-size: 12px; }
.ex-icon-btn:hover { background: #fff4e8; color: #c2410c; }
.ex-icon-btn.danger:hover { background: #fef2f2; color: #dc2626; }

.ex-empty { display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 40px 16px; color: #6b7280; font-size: 14px; text-align: center; }
.ex-empty strong { color: #111827; font-size: 16px; }
.ex-empty-icon { width: 54px; height: 54px; border-radius: 16px; background: #fff4e8; color: #FF9F43; display: flex; align-items: center; justify-content: center; font-size: 22px; margin-bottom: 4px; }

.ex-side { padding: 16px; position: sticky; top: 80px; }
.ex-side h5 { margin: 0 0 12px; font-size: 15px; font-weight: 700; color: #111827; }
.ex-side-empty { font-size: 13px; color: #9ca3af; }
.ex-cat { width: 100%; text-align: left; border: 1px solid transparent; background: transparent; border-radius: 10px; padding: 8px 10px; margin-bottom: 4px; }
.ex-cat:hover { background: #f9fafb; }
.ex-cat.on { border-color: #fed7aa; background: #fffaf5; }
.ex-cat-top { display: flex; justify-content: space-between; gap: 10px; font-size: 13px; color: #374151; }
.ex-cat-top span { display: inline-flex; align-items: center; gap: 6px; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ex-cat-top strong { font-variant-numeric: tabular-nums; }
.ex-bar { height: 6px; border-radius: 999px; background: #f1f2f4; margin: 6px 0 4px; overflow: hidden; }
.ex-bar span { display: block; height: 100%; border-radius: 999px; }
.ex-cat small { font-size: 11.5px; color: #9ca3af; }
.ex-modes-summary { margin-top: 14px; padding-top: 12px; border-top: 1px dashed #eef0f4; }
.ex-modes-summary h6 { margin: 0 0 8px; font-size: 12px; font-weight: 600; text-transform: uppercase; color: #6b7280; }
.ex-mode-row { display: flex; justify-content: space-between; font-size: 13px; color: #374151; padding: 4px 0; }
.ex-mode-row i { color: #9ca3af; width: 16px; }

@media (max-width: 1199px) {
  .ex-layout { grid-template-columns: 1fr; }
  .ex-side { position: static; order: -1; }
  .ex-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 576px) {
  .ex-kpis { grid-template-columns: 1fr; }
  .ex-modes button span { display: none; }
  .ex-row-actions { opacity: 1; }
}
</style>
